import { ChangeDetectionStrategy, Component, effect, forwardRef, inject, input, output, signal } from '@angular/core';
import { InputSize } from '../../../enum/input-size.enum';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import {
  NgxFloatUiContentComponent,
  NgxFloatUiDirective,
  NgxFloatUiPlacements,
  NgxFloatUiTriggers,
} from 'ngx-float-ui';
import { Country, LookupTypeRepository } from '../../../../../core';
import { maskitoTransform } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { PhoneNumberUtils } from '../../../../utils/phone-number-utils';

@Component({
  selector: 'lib-phone-input',
  imports: [MatIcon, NgxFloatUiContentComponent, NgxFloatUiDirective, MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent implements ControlValueAccessor {
  placeholder = input<string>('');
  controlId = input<string | null>(null);
  countryCodeFormControl = input.required<FormControl>();
  error = input<boolean>(false);
  inputSize = input<InputSize>(InputSize.large);
  showClearButton = input<boolean | null | undefined>();
  valueChange = output<string | null>();

  protected readonly InputSize = InputSize;
  protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
  protected readonly NgxFloatUiTriggers = NgxFloatUiTriggers;

  private lookupTypeRepository = inject(LookupTypeRepository);
  countries = signal<Country[]>([]);
  selectedCountryCode = signal<string>('CA');
  selectedCountryPhoneCode = signal<string>('+1');

  countryCodeFormControlEffect = effect(() => {
    this.countryCodeFormControl().setValue(this.selectedCountryCode());
    this.countryCodeFormControl().valueChanges.subscribe(value => {
      const country = this.countries().find(country => country.countryCode === value);
      if (country) {
        this.selectedCountryCode.set(country.countryCode);
        this.selectedCountryPhoneCode.set(country.phoneCode);
      }
    });
  });

  public value = signal<string | null>(null);
  public disabled = signal(false);
  protected readonly PhoneNumberUtils = PhoneNumberUtils;

  constructor() {
    this.lookupTypeRepository.getAllCountryTypes().subscribe(types => {
      this.countries.set(types.filter(type => type.phoneCode !== null));
    });
  }

  public writeValue(value: string | null): void {
    const updatedValue = maskitoTransform(value ?? '', PhoneNumberUtils.maskitoOptions10Digit);
    this.value.set(updatedValue);
    const unmaskedValue = updatedValue.replaceAll(' ', '-');
    this.valueChange.emit(unmaskedValue);
    this.onChange(unmaskedValue);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value.set(inputValue);
    this.valueChange.emit(inputValue);
    this.onChange(inputValue);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public countryClicked(country: Country) {
    this.countryCodeFormControl().setValue(country.countryCode);
  }

  private onChange: (value: string | null) => void = () => {
    // default onChange
  };
  private onTouched: () => void = () => {
    // default onTouched
  };
}
